/**
 * Runtime Keys Utility (Preview MVP)
 * 
 * Manages API keys for the preview environment.
 * Uses localStorage/sessionStorage only when VERCEL_ENV is 'preview'.
 * STRICT BYOK: In production, never writes to storage.
 */

const isPreview = import.meta.env.VITE_VERCEL_ENV === 'preview'
    || window.location.hostname.includes('vercel.app')
    || window.location.hostname.includes('localhost');

export const setGeminiKey = (key: string) => {
    if (!isPreview) {
        console.log('[RuntimeKeys] Prod mode: Ignoring storage write');
        return;
    }
    if (!key) return;

    localStorage.setItem('gemini_api_key', key);
    sessionStorage.setItem('gemini_api_key', key);
    console.log('[RuntimeKeys] Key saved to storage (Preview Only)');
};

export const getGeminiKey = (): string | null => {
    if (!isPreview) return null;
    return localStorage.getItem('gemini_api_key') ||
        sessionStorage.getItem('gemini_api_key') ||
        import.meta.env.VITE_GEMINI_API_KEY ||
        null;
};

export const clearGeminiKey = () => {
    if (!isPreview) return;
    localStorage.removeItem('gemini_api_key');
    sessionStorage.removeItem('gemini_api_key');
    console.log('[RuntimeKeys] Key cleared from storage');
};

export const hasGeminiKey = (): boolean => {
    return !!getGeminiKey();
};

/**
 * Server-side key status cache
 * Avoids repeated API calls within a session
 */
let serverKeyStatusCache: { gemini: boolean; source: string; timestamp: number } | null = null;
const CACHE_TTL_MS = 60000; // 1 minute cache

/**
 * Check if Gemini API key is available (server OR client BYOK)
 * This is the robust check that should be used for feature gating.
 *
 * @returns Promise<{ available: boolean; source: 'server' | 'byok' | 'none' }>
 */
export const checkGeminiKeyStatus = async (): Promise<{
    available: boolean;
    source: 'server' | 'byok' | 'none';
}> => {
    // First check local BYOK
    const localKey = getGeminiKey();
    if (localKey) {
        console.log('[RuntimeKeys] BYOK key available locally');
        return { available: true, source: 'byok' };
    }

    // Check cache
    if (serverKeyStatusCache && (Date.now() - serverKeyStatusCache.timestamp) < CACHE_TTL_MS) {
        return {
            available: serverKeyStatusCache.gemini,
            source: serverKeyStatusCache.source as 'server' | 'byok' | 'none'
        };
    }

    // Query server for key availability
    try {
        const response = await fetch('/api/keys/status', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                // Include BYOK if available for server-side validation
                ...(localKey ? { 'x-api-key': localKey } : {})
            }
        });

        if (!response.ok) {
            console.warn('[RuntimeKeys] Failed to check server key status');
            return { available: false, source: 'none' };
        }

        const data = await response.json();

        // Cache the result
        serverKeyStatusCache = {
            gemini: data.gemini,
            source: data.source,
            timestamp: Date.now()
        };

        console.log('[RuntimeKeys] Server key status:', data);
        return {
            available: data.gemini,
            source: data.source
        };
    } catch (error) {
        console.error('[RuntimeKeys] Error checking key status:', error);
        return { available: false, source: 'none' };
    }
};

/**
 * Invalidate the server key status cache
 * Call this when user saves a new API key
 */
export const invalidateKeyStatusCache = () => {
    serverKeyStatusCache = null;
};
